import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RefreshIcon from "@mui/icons-material/Refresh";

const ChatReportsSection = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("pending");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);

  // Modal state
  const [selectedReport, setSelectedReport] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    action: null,
    report: null,
  });

  useEffect(() => {
    fetchReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filterStatus]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page,
        limit: 50,
      });
      if (filterStatus !== "all") {
        params.append("status", filterStatus);
      }
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/chatreports?${params}`
      );
      setReports(response.data.reports);
      setTotalPages(response.data.totalPages);
      setTotal(response.data.total);
      setPendingCount(response.data.pendingCount);
    } catch (err) {
      console.error("Failed to fetch reports:", err);
    } finally {
      setLoading(false);
    }
  };

  const openConfirmDialog = (title, message, action, report) => {
    setConfirmDialog({
      open: true,
      title,
      message,
      action,
      report,
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({
      open: false,
      title: "",
      message: "",
      action: null,
      report: null,
    });
  };

  const handleConfirmAction = async () => {
    const { action, report } = confirmDialog;
    closeConfirmDialog();

    try {
      if (action === "dismiss") {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/admin/chatreports/${report.id}`,
          { status: "dismissed", action_taken: "Dismissed by admin" }
        );
      } else if (action === "delete_message") {
        await axios.delete(
          `${process.env.REACT_APP_API_URL}/api/admin/chatreports/${report.id}/delete-message`
        );
      } else if (action === "ban_user") {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/admin/chatreports/${report.id}/ban-user`
        );
      }
      fetchReports();
      setSelectedReport(null);
    } catch (err) {
      console.error("Failed to perform action:", err);
    }
  };

  const handleDismiss = (report) => {
    openConfirmDialog(
      "Dismiss Report",
      `Are you sure you want to dismiss this report? The message will remain visible.`,
      "dismiss",
      report
    );
  };

  const handleDeleteMessage = (report) => {
    openConfirmDialog(
      "Delete Message",
      `Are you sure you want to delete this message? This will mark the report as actioned.`,
      "delete_message",
      report
    );
  };

  const handleBanUser = (report) => {
    openConfirmDialog(
      "Ban User",
      `Are you sure you want to ban "${report.author_username || report.message_username}"? This will also delete the reported message.`,
      "ban_user",
      report
    );
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "#ff9800";
      case "reviewed":
        return "#2196f3";
      case "actioned":
        return "#4caf50";
      case "dismissed":
        return "#9e9e9e";
      default:
        return "#666";
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString();
  };

  if (loading && reports.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress sx={{ color: "#fff" }} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Stats */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}>
          <Paper sx={{ p: 1.5, backgroundColor: "#16213e", borderRadius: 1, border: "1px solid #394867", textAlign: "center" }}>
            <Typography variant="h5" sx={{ color: "#f44336", fontWeight: 700 }}>{pendingCount}</Typography>
            <Typography variant="caption" sx={{ color: "#9e9e9e" }}>Pending Reports</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={3}>
          <Paper sx={{ p: 1.5, backgroundColor: "#16213e", borderRadius: 1, border: "1px solid #394867", textAlign: "center" }}>
            <Typography variant="h5" sx={{ color: "#4caf50", fontWeight: 700 }}>{total}</Typography>
            <Typography variant="caption" sx={{ color: "#9e9e9e" }}>Total Reports</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Filter */}
      <Paper sx={{ p: 2, mb: 2, backgroundColor: "#16213e", borderRadius: 2, border: "1px solid #394867" }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={10} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ color: "#9e9e9e" }}>Status</InputLabel>
              <Select
                value={filterStatus}
                label="Status"
                onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
                sx={{
                  color: "#fff",
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#394867" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#541b14" },
                  "& .MuiSvgIcon-root": { color: "#9e9e9e" },
                }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="reviewed">Reviewed</MenuItem>
                <MenuItem value="actioned">Actioned</MenuItem>
                <MenuItem value="dismissed">Dismissed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={2} md={8} sx={{ display: "flex", justifyContent: "flex-end" }}>
            <IconButton
              onClick={fetchReports}
              disabled={loading}
              sx={{ color: "#9e9e9e", "&:hover": { color: "#fff", backgroundColor: "#394867" } }}
              title="Refresh"
            >
              <RefreshIcon sx={{ animation: loading ? "spin 1s linear infinite" : "none", "@keyframes spin": { "0%": { transform: "rotate(0deg)" }, "100%": { transform: "rotate(360deg)" } } }} />
            </IconButton>
          </Grid>
        </Grid>
      </Paper>

      {/* Reports Table */}
      <Paper sx={{ backgroundColor: "#16213e", borderRadius: 2, border: "1px solid #394867", overflow: "hidden" }}>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ backgroundColor: "#0f1729", color: "#9e9e9e", fontWeight: 600, borderBottom: "1px solid #394867", py: 1 }}>Reported</TableCell>
                <TableCell sx={{ backgroundColor: "#0f1729", color: "#9e9e9e", fontWeight: 600, borderBottom: "1px solid #394867", py: 1 }}>Message</TableCell>
                <TableCell sx={{ backgroundColor: "#0f1729", color: "#9e9e9e", fontWeight: 600, borderBottom: "1px solid #394867", py: 1 }}>Author</TableCell>
                <TableCell sx={{ backgroundColor: "#0f1729", color: "#9e9e9e", fontWeight: 600, borderBottom: "1px solid #394867", py: 1 }}>Room</TableCell>
                <TableCell sx={{ backgroundColor: "#0f1729", color: "#9e9e9e", fontWeight: 600, borderBottom: "1px solid #394867", py: 1 }}>Reporter</TableCell>
                <TableCell sx={{ backgroundColor: "#0f1729", color: "#9e9e9e", fontWeight: 600, borderBottom: "1px solid #394867", py: 1 }}>Status</TableCell>
                <TableCell align="center" sx={{ backgroundColor: "#0f1729", color: "#9e9e9e", fontWeight: 600, borderBottom: "1px solid #394867", py: 1 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reports.map((report) => (
                <TableRow
                  key={report.id}
                  hover
                  sx={{
                    "&:hover": { backgroundColor: "#1a1a2e" },
                    opacity: report.message_is_deleted ? 0.5 : 1,
                  }}
                >
                  <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867", py: 0.75, fontSize: "0.75rem" }}>
                    {formatDate(report.created_at)}
                  </TableCell>
                  <TableCell sx={{ color: "#fff", borderBottom: "1px solid #394867", py: 0.75, maxWidth: 300 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        textDecoration: report.message_is_deleted ? "line-through" : "none",
                      }}
                      title={report.message_content}
                    >
                      {report.message_content || "[deleted]"}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ color: "#fff", borderBottom: "1px solid #394867", py: 0.75 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      {report.author_username || report.message_username || "-"}
                      {report.author_is_banned && (
                        <Chip
                          label="Banned"
                          size="small"
                          sx={{ height: 16, fontSize: "0.6rem", backgroundColor: "#b71c1c", color: "#fff" }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867", py: 0.75 }}>
                    {report.room_name || "-"}
                  </TableCell>
                  <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867", py: 0.75 }}>
                    {report.reporter_username || "-"}
                  </TableCell>
                  <TableCell sx={{ borderBottom: "1px solid #394867", py: 0.75 }}>
                    <Chip
                      label={report.status}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.7rem",
                        backgroundColor: getStatusColor(report.status),
                        color: "#fff",
                        textTransform: "capitalize",
                      }}
                    />
                  </TableCell>
                  <TableCell align="center" sx={{ borderBottom: "1px solid #394867", py: 0.75 }}>
                    {report.status === "pending" && (
                      <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5 }}>
                        <IconButton
                          size="small"
                          onClick={() => setSelectedReport(report)}
                          sx={{ color: "#2196f3", p: 0.5 }}
                          title="View Details"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteMessage(report)}
                          sx={{ color: "#ff5722", p: 0.5 }}
                          title="Delete Message"
                          disabled={report.message_is_deleted}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleBanUser(report)}
                          sx={{ color: "#f44336", p: 0.5 }}
                          title="Ban User"
                          disabled={report.author_is_banned || !report.message_guest_id}
                        >
                          <BlockIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDismiss(report)}
                          sx={{ color: "#9e9e9e", p: 0.5 }}
                          title="Dismiss"
                        >
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                    {report.status !== "pending" && (
                      <Typography variant="caption" sx={{ color: "#666" }}>
                        {report.action_taken || "-"}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {reports.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} sx={{ color: "#666", textAlign: "center", py: 4, borderBottom: "1px solid #394867" }}>
                    No reports found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 2, p: 2, borderTop: "1px solid #394867" }}>
            <Button
              size="small"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
              sx={{ color: "#fff" }}
            >
              Previous
            </Button>
            <Typography variant="body2" sx={{ color: "#9e9e9e" }}>
              Page {page} of {totalPages}
            </Typography>
            <Button
              size="small"
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
              sx={{ color: "#fff" }}
            >
              Next
            </Button>
          </Box>
        )}
      </Paper>

      {/* Detail Modal */}
      <Dialog
        open={!!selectedReport}
        onClose={() => setSelectedReport(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "#1a1a2e",
            border: "1px solid #394867",
            borderRadius: 2,
          },
        }}
      >
        {selectedReport && (
          <>
            <DialogTitle sx={{ color: "#fff", borderBottom: "1px solid #394867" }}>
              Report Details
            </DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ color: "#9e9e9e", mb: 0.5 }}>Message Content</Typography>
                  <Paper sx={{ p: 2, backgroundColor: "#16213e", border: "1px solid #394867" }}>
                    <Typography sx={{ color: "#fff", wordBreak: "break-word" }}>
                      {selectedReport.message_content || "[deleted]"}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" sx={{ color: "#9e9e9e" }}>Author</Typography>
                  <Typography sx={{ color: "#fff" }}>{selectedReport.author_username || selectedReport.message_username || "-"}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" sx={{ color: "#9e9e9e" }}>Reporter</Typography>
                  <Typography sx={{ color: "#fff" }}>{selectedReport.reporter_username || "-"}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" sx={{ color: "#9e9e9e" }}>Room</Typography>
                  <Typography sx={{ color: "#fff" }}>{selectedReport.room_name || "-"}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" sx={{ color: "#9e9e9e" }}>Message Date</Typography>
                  <Typography sx={{ color: "#fff" }}>{formatDate(selectedReport.message_created_at)}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ color: "#9e9e9e" }}>Report Date</Typography>
                  <Typography sx={{ color: "#fff" }}>{formatDate(selectedReport.created_at)}</Typography>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2, borderTop: "1px solid #394867", gap: 1 }}>
              <Button onClick={() => setSelectedReport(null)} sx={{ color: "#9e9e9e" }}>
                Close
              </Button>
              <Button
                onClick={() => handleDismiss(selectedReport)}
                variant="outlined"
                sx={{ color: "#9e9e9e", borderColor: "#9e9e9e" }}
              >
                Dismiss
              </Button>
              <Button
                onClick={() => handleDeleteMessage(selectedReport)}
                variant="contained"
                color="warning"
                disabled={selectedReport.message_is_deleted}
                startIcon={<DeleteIcon />}
              >
                Delete Message
              </Button>
              <Button
                onClick={() => handleBanUser(selectedReport)}
                variant="contained"
                color="error"
                disabled={selectedReport.author_is_banned || !selectedReport.message_guest_id}
                startIcon={<BlockIcon />}
              >
                Ban User
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={closeConfirmDialog}
        PaperProps={{
          sx: {
            backgroundColor: "#1a1a2e",
            border: "1px solid #394867",
            borderRadius: 2,
            minWidth: 400,
          },
        }}
      >
        <DialogTitle sx={{ color: "#fff", borderBottom: "1px solid #394867" }}>
          {confirmDialog.title}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <DialogContentText sx={{ color: "#9e9e9e" }}>
            {confirmDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: "1px solid #394867" }}>
          <Button onClick={closeConfirmDialog} sx={{ color: "#9e9e9e" }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            variant="contained"
            sx={{
              backgroundColor: confirmDialog.action === "dismiss" ? "#9e9e9e" : "#ef5350",
              "&:hover": {
                backgroundColor: confirmDialog.action === "dismiss" ? "#757575" : "#d32f2f",
              },
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChatReportsSection;
